import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { appConfig } from '@/core/config';
import { useOperatorStore } from '@/core/stores/operator-store';
import {
  MediaListParams,
  MediaListResponse,
  MediaResponse,
  DeleteMediaResponse,
  CopySoundsResult,
} from '../types/sound-management-types';
import { MediaType } from '../types/translation-types';

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:audio/mpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Sound Management API
 * Handles all API calls for managing sound media files
 */
export class SoundManagementApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeadersWithOperator(): Promise<Record<string, string>> {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      console.error('SoundManagementApi: No operator selected, preventing API call');
      throw new Error('No operator selected. Please select an operator first.');
    }

    const headers = await this.tokenManager.getAuthHeaders();

    return {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': operatorId.toString(),
    };
  }

  /**
   * Get media/sounds list with filtering
   * API: GET /api/v1/translations/media?applicationType={1-7}&mediaType=4&languageCode={string}&skip={int}&take={int}
   */
  async getMediaList(params: MediaListParams): Promise<MediaListResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    // Build query string
    const queryParts: string[] = [
      `applicationType=${params.applicationType}`,
      `mediaType=${params.mediaType}`,
    ];

    if (params.languageCode) {
      queryParts.push(`languageCode=${encodeURIComponent(params.languageCode)}`);
    }

    if (params.skip !== undefined) {
      queryParts.push(`skip=${params.skip}`);
    }

    if (params.take !== undefined) {
      queryParts.push(`take=${params.take}`);
    }

    const env = appConfig.getRuntimeConfig()?.environment ?? appConfig.getEnvironment();
    queryParts.push(`environment=${env === 'prod' ? 2 : 1}`);

    const url = `${this.baseUrl}/translations/media?${queryParts.join('&')}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sound Management API',
      '/translations/media'
    );

    const data = await response.json();

    // Handle response format: { isSuccess, items, totalCount }
    if (data.items !== undefined) {
      return {
        isSuccess: data.isSuccess ?? true,
        rows: data.items,
        count: data.totalCount ?? data.items.length,
      };
    }

    // Handle response format: { isSuccess, rows, count }
    if (data.rows !== undefined) {
      return {
        isSuccess: data.isSuccess ?? true,
        rows: data.rows,
        count: data.count ?? data.rows.length,
      };
    }

    // Handle array format
    if (Array.isArray(data)) {
      return {
        isSuccess: true,
        rows: data,
        count: data.length,
      };
    }

    // Handle standard response format
    return data;
  }

  /**
   * Get a single media item by ID
   * API: GET /api/v1/translations/media/{mediaId}
   */
  async getMediaById(mediaId: number): Promise<MediaResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/media/${mediaId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sound Management API',
      `/translations/media/${mediaId}`
    );

    const data = await response.json();

    // Handle wrapped response
    if (data.media) {
      return {
        isSuccess: true,
        media: data.media,
      };
    }

    return data;
  }

  /**
   * Upload a sound file
   * API: POST /api/v1/translations/media
   * Content-Type: application/json with base64-encoded file content
   */
  async uploadMedia(
    file: File,
    applicationType: number,
    mediaType: MediaType,
    languageCode: string,
    keyName?: string
  ): Promise<MediaResponse> {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      throw new Error('No operator selected. Please select an operator first.');
    }

    const authHeaders = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}/translations/media`;

    // Convert file to base64
    const fileContent = await fileToBase64(file);

    // Determine content type
    const contentType = file.type || 'audio/mpeg';

    const env = appConfig.getRuntimeConfig()?.environment ?? appConfig.getEnvironment();
    const requestBody = {
      applicationType,
      mediaType,
      languageCode,
      fileName: file.name,
      fileContent,
      contentType,
      environment: env === 'prod' ? 2 : 1,
      ...(keyName && { keyName }),
    };

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          'X-Operator-Id': operatorId.toString(),
        },
        body: JSON.stringify(requestBody),
      },
      'Sound Management API',
      '/translations/media'
    );

    const result = await response.json();

    // Check for API-level validation errors
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to upload sound file');
    }

    return result;
  }

  /**
   * Delete a media item
   * API: DELETE /api/v1/translations/media/{mediaId}
   */
  async deleteMedia(mediaId: number): Promise<DeleteMediaResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/media/${mediaId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'DELETE',
        headers,
      },
      'Sound Management API',
      `/translations/media/${mediaId}`
    );

    const result = await response.json();
    return result;
  }

  /**
   * Copy all sound files from baseLanguageCode to newLanguageCode, across all
   * application types. Skips any sound newLanguageCode already has of its own.
   * API: POST /api/v1/translations/languages/{newLanguageCode}/copy-sounds-from/{baseLanguageCode}
   */
  async copySounds(
    newLanguageCode: string,
    baseLanguageCode: string
  ): Promise<CopySoundsResult> {
    const headers = await this.getAuthHeadersWithOperator();
    const env = appConfig.getRuntimeConfig()?.environment ?? appConfig.getEnvironment();
    const environment = env === 'prod' ? 2 : 1;
    const url = `${this.baseUrl}/translations/languages/${encodeURIComponent(
      newLanguageCode
    )}/copy-sounds-from/${encodeURIComponent(baseLanguageCode)}?environment=${environment}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
      },
      'Sound Management API',
      `/translations/languages/${newLanguageCode}/copy-sounds-from/${baseLanguageCode}`
    );

    const result = await response.json();

    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || result.message || 'Failed to copy sound files');
    }

    return result;
  }
}
